/**
 * Label badge layout for floor-plan elements (extracted from `upp-floor-plan` for coverage and clarity).
 */

export interface FloorPlanLabelBadgeLine {
  readonly text: string;
  readonly size: number;
}

export interface FloorPlanLabelBadgeBoxLayout {
  readonly lines: FloorPlanLabelBadgeLine[];
  readonly boxW: number;
  readonly boxH: number;
}

const LABEL_PRIMARY_SIZE_SCALE = 1.25;
const LABEL_SUBLABEL_SIZE_SCALE = 0.82;
const LABEL_LINE_GAP = 2;
const LABEL_PAD_X = 8;
const LABEL_PAD_Y = 6;

/**
 * @returns `null` when both label strings are empty after trim.
 */
export function floorPlanElementLabelBadgeBoxLayout(params: {
  primary: string;
  secondary: string;
  baseFontSize: number;
  measureTextWidth: (text: string, fontSize: number) => number;
}): FloorPlanLabelBadgeBoxLayout | null {
  const primary = params.primary.trim();
  const secondary = params.secondary.trim();
  if (!primary && !secondary) {
    return null;
  }
  const baseFs = params.baseFontSize;
  const primaryFs = Math.max(11, Math.round(baseFs * LABEL_PRIMARY_SIZE_SCALE));
  const subFs = Math.max(10, Math.round(baseFs * LABEL_SUBLABEL_SIZE_SCALE));
  const lines: FloorPlanLabelBadgeLine[] = [];
  if (primary) {
    lines.push({ text: primary, size: primaryFs });
  }
  if (secondary) {
    lines.push({ text: secondary, size: subFs });
  }
  let innerH = 0;
  for (let i = 0; i < lines.length; i += 1) {
    innerH += (lines[i]?.size ?? 0) + (i > 0 ? LABEL_LINE_GAP : 0);
  }
  let maxTextW = 0;
  for (const ln of lines) {
    maxTextW = Math.max(maxTextW, params.measureTextWidth(ln.text, ln.size));
  }
  const boxW = maxTextW + 2 * LABEL_PAD_X;
  const boxH = innerH + 2 * LABEL_PAD_Y;
  return { lines, boxW, boxH };
}
